import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContextAWS';
import { usePlayerProfile } from '../../contexts/PlayerProfileContext';
import draftService from '../../services/draftService';
import playerService from '../../services/playerService';
import {
  getRosteredPlayers,
  getRosterForDate,
  addPlayerToRoster,
  saveRosterForDate,
  getCurrentDate
} from '../../utils/rosterManager';
import DraftBoard from '../../components/DraftBoard';
import PlayerRow from '../../components/PlayerRow';
import {
  initializeDraft,
  getCurrentPickInfo,
  getNextPickInfo,
  makePick,
  getAvailablePlayers,
  getTeamDraftPicks,
  getTimeRemaining
} from '../../utils/draftManager';
import { CheckIcon, HistoryIcon, TargetIcon, DiceIcon, PlayIcon, PauseIcon } from '../../components/icons/StreamlineFlexSolid';

function DraftRoom() {
  const { league } = useOutletContext();
  const { user } = useAuth();
  const { openPlayerProfile } = usePlayerProfile();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allPlayers, setAllPlayers] = useState([]);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(90);
  const [recentPicks, setRecentPicks] = useState([]);
  const [myPicks, setMyPicks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [error, setError] = useState('');
  const [playerLoadError, setPlayerLoadError] = useState('');
  const [retryingPlayers, setRetryingPlayers] = useState(false);
  const [boardMinimized, setBoardMinimized] = useState(true);
  const [togglingAutoPick, setTogglingAutoPick] = useState(false);

  const draftSubscriptionRef = useRef(null);
  const pickSubscriptionRef = useRef(null);
  const timerRef = useRef(null);
  const allPlayersRef = useRef([]);
  const lastAutoPickPickNumberRef = useRef(-1);
  const handleAutoPickRef = useRef(() => {});
  const loadedLeagueIdRef = useRef(null);

  // Validate league data
  if (!league) {
    console.error('❌ League data is missing');
    return (
      <div className="empty-state">
        <h2>Error</h2>
        <p style={{ color: 'var(--danger)' }}>League data not found. Please go back and try again.</p>
      </div>
    );
  }

  if (!league.teams || !Array.isArray(league.teams)) {
    console.error('❌ League teams data is invalid:', league);
    return (
      <div className="empty-state">
        <h2>Error</h2>
        <p style={{ color: 'var(--danger)' }}>League has no teams configured. Please configure teams first.</p>
      </div>
    );
  }

  const userId = user?.userId || user?.id || user?.sub;
  const myTeam = league.teams.find(t => t.owner === userId);
  const isCommissioner = league?.creatorId === userId;

  if (!myTeam) {
    console.error('❌ User is not part of this league. userId:', userId, 'teams:', league.teams);
  }

  // Load draft state and players
  useEffect(() => {
    // Guard against StrictMode's dev-mode double-invoke of effects: without
    // this, two concurrent loadDraftAndPlayers() calls can both find no
    // existing draft and both call createDraft, and the second one's
    // conditional write loses the race and logs a spurious error even though
    // the app already recovers fine (one draft ends up created either way).
    if (loadedLeagueIdRef.current === league.id) return undefined;
    loadedLeagueIdRef.current = league.id;
    loadDraftAndPlayers();
    return () => {
      // Cleanup subscriptions on unmount
      if (draftSubscriptionRef.current) {
        draftSubscriptionRef.current.unsubscribe();
      }
      if (pickSubscriptionRef.current) {
        pickSubscriptionRef.current.unsubscribe();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [league.id]);

  useEffect(() => {
    allPlayersRef.current = allPlayers;
  }, [allPlayers]);

  const loadDraftAndPlayers = async () => {
    try {
      setLoading(true);
      setError('');
      setPlayerLoadError('');

      console.log('🔄 Loading draft and players...');
      console.log('📋 League ID:', league.id || league.leagueId);
      console.log('👥 League Teams:', league.teams);
      
      const leagueId = league.id || league.leagueId;

      // 1. Fetch draft state first (needed to exclude already-drafted players)
      console.log('📡 Fetching draft state for league:', leagueId);
      let draftState = await draftService.getDraft(leagueId);
      
      if (!draftState) {
        console.log('📝 No existing draft found, will create after loading players...');
        // Validate league data before we continue
        if (!league.teams || league.teams.length === 0) {
          throw new Error('Cannot initialize draft: League has no teams');
        }
      } else {
        console.log('✅ Draft state loaded:', draftState);
      }
      
      // 2. Build exclude list: rostered + already drafted
      const rosteredPlayerIds = getRosteredPlayers(leagueId);
      if (draftState?.picks && Array.isArray(draftState.picks)) {
        draftState.picks.forEach(pick => {
          if (pick.playerId) {
            rosteredPlayerIds.push(pick.playerId.toString());
          }
        });
      }
      const excludeIds = [...new Set(rosteredPlayerIds)];
      
      // 3. Fetch players (DB first, API fallback)
      console.log('📦 Loading players (DB first, API fallback)...');
      const playerResult = await playerService.getDraftPlayers({
        excludeRostered: excludeIds,
        filters: { activeOnly: true },
      });

      let players = [];
      let playerError = null;
      if (playerResult.success && playerResult.players && playerResult.players.length > 0) {
        console.log(`✅ Loaded ${playerResult.players.length} players from ${playerResult.source || 'unknown source'}`);
        players = playerResult.players;
      } else {
        playerError = playerResult?.error || 'Could not load players.';
        console.warn('⚠️ Player fetch failed or empty:', playerError);
      }
      setAllPlayers(players);
      allPlayersRef.current = players;
      if (playerError) {
        const actionable =
          playerError.includes('API key') || playerError.includes('FantasyData') || playerError.includes('quota')
            ? playerError
            : `${playerError} Sync players from League Settings → Rosters, or configure the FantasyData API key.`;
        setPlayerLoadError('Could not load players. ' + actionable);
      } else {
        setPlayerLoadError('');
      }

      // 4. Create draft if none exists
      if (!draftState) {
        const newDraft = initializeDraft(league, league.settings?.draftSettings);
        console.log('📝 Initialized draft:', newDraft);
        draftState = await draftService.createDraft(newDraft);
        console.log('✅ Draft created successfully');
      }

      setDraft(draftState);
      updateAvailablePlayers(draftState, players);

      // 5. Set up real-time subscriptions
      console.log('📡 Setting up real-time subscriptions...');
      setupSubscriptions(draftState.leagueId);
      console.log('✅ Draft room loaded successfully');
      
    } catch (error) {
      console.error('❌ Error loading draft:', error);
      
      // Handle GraphQL errors
      if (error.errors && Array.isArray(error.errors)) {
        console.error('❌ GraphQL Errors:', error.errors);
        error.errors.forEach((err, index) => {
          console.error(`❌ Error ${index + 1}:`, err.message);
          if (err.errorType) console.error(`   Type: ${err.errorType}`);
          if (err.path) console.error(`   Path: ${err.path}`);
        });
      }
      
      // Extract meaningful error message
      let errorMessage = 'Failed to load draft. ';
      
      if (error.message) {
        console.error('❌ Error message:', error.message);
        errorMessage += error.message;
      } else if (error.errors && error.errors.length > 0) {
        const graphqlError = error.errors[0].message;
        console.error('❌ GraphQL error message:', graphqlError);
        errorMessage += graphqlError;
      } else {
        errorMessage += 'Unknown error occurred.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Setup real-time subscriptions
  const setupSubscriptions = (leagueId) => {
    // Subscribe to draft state updates
    draftSubscriptionRef.current = draftService.subscribeToDraftUpdates(
      leagueId,
      (updatedDraft) => {
        console.log('📡 Draft updated:', updatedDraft);
        setDraft(updatedDraft);
        // Use ref to avoid stale closure over allPlayers
        updateAvailablePlayers(updatedDraft, allPlayersRef.current);
      }
    );

    // Subscribe to new picks (compare teamId robustly: id vs teamId, string vs number)
    const myTeamId = myTeam != null ? (myTeam.id ?? myTeam.teamId) : null;
    pickSubscriptionRef.current = draftService.subscribeToDraftPicks(
      leagueId,
      (newPick) => {
        console.log('📡 New pick:', newPick);
        setRecentPicks(prev => [newPick, ...prev].slice(0, 10));

        const isMyPick = myTeamId != null && String(newPick.teamId) === String(myTeamId);
        if (isMyPick && newPick.playerId) {
          const uid = user?.userId || user?.id || user?.sub;
          if (uid && league) {
            (async () => {
              try {
                const roster = await getRosterForDate(uid, leagueId, getCurrentDate(), league);
                const player = {
                  id: newPick.playerId,
                  name: newPick.playerName || 'Unknown',
                  position: newPick.playerPosition || 'UTIL',
                  team: newPick.playerTeam || 'FA',
                  stats: { battingAverage: 0, homeRuns: 0, rbi: 0, stolenBases: 0 }
                };
                const res = addPlayerToRoster(roster, player);
                if (res.success) await saveRosterForDate(uid, leagueId, res.roster, getCurrentDate(), league);
              } catch (e) {
                console.warn('Sync draft pick to roster failed:', e);
              }
            })();
          }
        }

        if (!isMyPick) {
          showPickNotification(newPick);
        }
      }
    );
  };

  // Update available players based on draft state
  const updateAvailablePlayers = (draftState, playersList) => {
    const picks = draftState?.picks ?? [];
    const draftedIds = new Set(
      picks.map(p => (p.playerId != null ? String(p.playerId) : null)).filter(Boolean)
    );
    const list = Array.isArray(playersList) ? playersList : [];
    const available = list.filter(p => {
      const id = p?.id != null ? String(p.id) : null;
      return id && !draftedIds.has(id);
    });
    setAvailablePlayers(available);
    setFilteredPlayers(available);
  };

  const retryLoadPlayers = async () => {
    if (!draft || retryingPlayers) return;
    setRetryingPlayers(true);
    setPlayerLoadError('');
    const leagueId = league?.id || league?.leagueId;
    const rostered = getRosteredPlayers(leagueId);
    const pickIds = (draft.picks || []).map(p => p.playerId?.toString()).filter(Boolean);
    const excludeIds = [...new Set([...rostered, ...pickIds])];
    try {
      const res = await playerService.getDraftPlayers({
        excludeRostered: excludeIds,
        filters: { activeOnly: true },
      });
      if (res.success && res.players && res.players.length > 0) {
        setAllPlayers(res.players);
        allPlayersRef.current = res.players;
        updateAvailablePlayers(draft, res.players);
        setPlayerLoadError('');
      } else {
        setPlayerLoadError(res?.error || 'Could not load players. Sync from League Settings → Rosters or configure FantasyData API key.');
      }
    } catch (e) {
      setPlayerLoadError(e?.message || 'Could not load players. Try again.');
    } finally {
      setRetryingPlayers(false);
    }
  };

  // Filter players based on search and filters
  useEffect(() => {
    let filtered = availablePlayers;

    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (positionFilter) {
      filtered = filtered.filter(p => p.position === positionFilter);
    }

    if (teamFilter) {
      filtered = filtered.filter(p => p.team === teamFilter);
    }

    setFilteredPlayers(filtered);
  }, [searchTerm, positionFilter, teamFilter, availablePlayers]);

  // Pick timer + auto-pick on timeout (put team on auto-draft)
  useEffect(() => {
    if (draft?.status === 'in_progress' && draft.pickStartTime) {
      timerRef.current = setInterval(() => {
        const remaining = getTimeRemaining(draft);
        setTimeRemaining(remaining);

        if (remaining > 0 || !draft.settings?.autoPickEnabled) return;
        const currentPick = getCurrentPickInfo(draft);
        if (!currentPick || availablePlayers.length === 0) return;
        if (lastAutoPickPickNumberRef.current === draft.currentPickNumber) return;

        lastAutoPickPickNumberRef.current = draft.currentPickNumber;
        handleAutoPickRef.current('timeout');
      }, 1000);

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    }
  }, [draft?.pickStartTime, draft?.status, draft?.currentPickNumber, availablePlayers.length]);

  // Immediate auto-pick when it's an auto-draft team's turn (they timed out earlier)
  useEffect(() => {
    if (draft?.status !== 'in_progress' || availablePlayers.length === 0) return;
    const currentPick = getCurrentPickInfo(draft);
    if (!currentPick) return;
    const autoIds = draft.settings?.autoDraftTeamIds || [];
    if (!autoIds.includes(currentPick.teamId)) return;
    if (lastAutoPickPickNumberRef.current === draft.currentPickNumber) return;

    lastAutoPickPickNumberRef.current = draft.currentPickNumber;
    handleAutoPickRef.current('auto_draft');
  }, [draft?.status, draft?.currentPickNumber, draft?.settings?.autoDraftTeamIds, draft?.pickStartTime, availablePlayers.length]);

  // Update my picks when draft changes
  useEffect(() => {
    if (draft && myTeam) {
      const picks = (draft.picks || []).filter(p => p.teamId === myTeam.id);
      setMyPicks(picks);
    }
  }, [draft?.picks, myTeam]);

  const handleStartDraft = async () => {
    if (!isCommissioner) return;
    
    try {
      const leagueId = league.id || league.leagueId;
      const updatedDraft = await draftService.startDraft(leagueId);
      setDraft(updatedDraft);
    } catch (error) {
      console.error('Error starting draft:', error);
      alert('Failed to start draft');
    }
  };

  const handlePauseDraft = async () => {
    if (!isCommissioner) return;
    try {
      const leagueId = league.id || league.leagueId;
      const updatedDraft = await draftService.pauseDraft(leagueId);
      setDraft(updatedDraft);
    } catch (error) {
      console.error('Error pausing draft:', error);
      alert('Failed to pause draft');
    }
  };

  const handleResumeDraft = async () => {
    if (!isCommissioner) return;
    try {
      const leagueId = league.id || league.leagueId;
      const updatedDraft = await draftService.resumeDraft(leagueId);
      setDraft(updatedDraft);
    } catch (error) {
      console.error('Error resuming draft:', error);
      alert('Failed to resume draft');
    }
  };

  const handleToggleAutoPick = async () => {
    if (!myTeam || togglingAutoPick) return;
    const next = !(draft?.settings?.autoDraftTeamIds || []).includes(myTeam.id);
    try {
      setTogglingAutoPick(true);
      const leagueId = league.id || league.leagueId;
      const updatedDraft = await draftService.toggleAutoPick(leagueId, draft, myTeam.id, next);
      setDraft(updatedDraft);
    } catch (err) {
      console.error('Error toggling auto-pick:', err);
      alert('Failed to update auto-pick');
    } finally {
      setTogglingAutoPick(false);
    }
  };

  const handleMakePick = async (player) => {
    const currentPick = getCurrentPickInfo(draft);
    
    if (!currentPick) {
      alert('Invalid draft state');
      return;
    }

    // Check if it's user's turn
    if (currentPick.teamId !== myTeam?.id) {
      alert("It's not your turn!");
      return;
    }

    try {
      const pickData = {
        pickNumber: currentPick.pickNumber,
        round: currentPick.round,
        pickInRound: currentPick.pickInRound,
        teamId: myTeam.id,
        teamName: myTeam.name,
        playerId: player.id,
        playerName: player.name,
        playerPosition: player.position,
        playerTeam: player.team,
        timestamp: new Date().toISOString(),
        pickType: 'manual'
      };

      // Update local draft state
      const pickResult = makePick(draft, player.id, player);
      
      if (!pickResult.success) {
        alert(pickResult.error);
        return;
      }

      // Sync to backend (triggers subscription for all users)
      const leagueId = league.id || league.leagueId;
      await draftService.makeDraftPick(leagueId, pickData, pickResult.draft);
      
      setSelectedPlayer(null);
      
    } catch (error) {
      console.error('Error making pick:', error);
      alert('Failed to make pick');
    }
  };

  const handleAutoPick = async (reason = 'auto_draft') => {
    const currentPick = getCurrentPickInfo(draft);
    if (!currentPick || availablePlayers.length === 0) return;

    const player = availablePlayers[0];
    const isTimeout = reason === 'timeout';
    const draftToUse = isTimeout
      ? {
          ...draft,
          settings: {
            ...draft.settings,
            autoDraftTeamIds: [
              ...new Set([...(draft.settings?.autoDraftTeamIds || []), currentPick.teamId])
            ]
          }
        }
      : draft;

    try {
      const pickData = {
        pickNumber: currentPick.pickNumber,
        round: currentPick.round,
        pickInRound: currentPick.pickInRound,
        teamId: currentPick.teamId,
        teamName: currentPick.team.name,
        playerId: player.id,
        playerName: player.name,
        playerPosition: player.position,
        playerTeam: player.team,
        timestamp: new Date().toISOString(),
        pickType: 'auto'
      };

      const pickResult = makePick(draftToUse, player.id, player);
      const leagueId = league.id || league.leagueId;
      await draftService.makeDraftPick(leagueId, pickData, pickResult.draft);
    } catch (error) {
      console.error('Error with auto-pick:', error);
      lastAutoPickPickNumberRef.current = -1;
    }
  };
  handleAutoPickRef.current = handleAutoPick;

  const showPickNotification = (pick) => {
    // Could implement a toast notification here
    console.log(`${pick.teamName} selected ${pick.playerName}`);
  };

  if (loading) {
    return (
      <div className="empty-state">
        <h2>Loading Draft Room...</h2>
        <p>Connecting to live draft...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <h2>Error</h2>
        <p style={{ color: 'var(--danger)' }}>{error}</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="empty-state">
        <h2>Draft Not Found</h2>
      </div>
    );
  }

  const currentPick = getCurrentPickInfo(draft);
  const nextPick = getNextPickInfo(draft);
  const isMyTurn = currentPick?.teamId === myTeam?.id;
  const isAutoPickOn = (draft?.settings?.autoDraftTeamIds || []).includes(myTeam?.id);

  return (
    <div>
      {playerLoadError && (
        <div className="alert alert-warning" style={{ marginBottom: '16px' }}>
          <p style={{ margin: 0, flex: '1 1 260px' }}>{playerLoadError}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={retryLoadPlayers}
            disabled={retryingPlayers}
            style={{ flexShrink: 0 }}
          >
            {retryingPlayers ? 'Loading…' : 'Retry loading players'}
          </button>
        </div>
      )}
      {/* Top card: draft status, round/pick, commissioner controls */}
      <div
        className="card"
        style={{
          marginBottom: '20px',
          backgroundColor: currentPick && draft.status === 'in_progress' && isMyTurn ? 'var(--success-soft)' : undefined,
          border: currentPick && draft.status === 'in_progress' && isMyTurn ? '3px solid var(--success)' : undefined
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
          <div style={{ flex: '1 1 280px', minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: '1.35rem' }}>
              {draft.status === 'not_started' && <><HistoryIcon size={18} /> Draft Not Started</>}
              {draft.status === 'in_progress' && <><span style={{ color: 'var(--danger)', marginRight: '6px' }}>●</span> LIVE</>}
              {draft.status === 'paused' && <><HistoryIcon size={18} /> Paused</>}
              {draft.status === 'completed' && <><CheckIcon size={18} style={{ color: 'var(--success)' }} /> Draft Complete</>}
            </h2>
            {currentPick && (draft.status === 'in_progress' || draft.status === 'paused') && (
              <div style={{ marginTop: '8px', color: 'var(--muted)', fontSize: '15px' }}>
                <span>
                  Round {currentPick.round}, Pick {currentPick.pickInRound} of {draft.teams.length}
                  {draft.status === 'in_progress' && ` · Pick #${currentPick.pickNumber}`}
                </span>
                {draft.status === 'in_progress' && (
                  <>
                    <div style={{ marginTop: '4px', fontWeight: '600', color: isMyTurn ? 'var(--success)' : 'var(--foreground)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {isMyTurn ? (
                        <><TargetIcon size={15} /> YOUR TURN!</>
                      ) : (
                        <><HistoryIcon size={14} /> {currentPick.team.name} is picking...</>
                      )}
                    </div>
                    {nextPick && (
                      <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '2px' }}>
                        Next: {nextPick.team.name}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          {draft.status === 'in_progress' && currentPick && (
            <div style={{ textAlign: 'center', flexShrink: 0 }}>
              <div
                style={{
                  fontSize: '28px',
                  fontWeight: 'bold',
                  color: timeRemaining <= 10 ? 'var(--danger)' : 'var(--primary)',
                  fontFamily: 'var(--font-display)',
                  fontVariantNumeric: 'tabular-nums'
                }}
              >
                {timeRemaining}s
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>Time left</div>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
            {isCommissioner && draft.status === 'not_started' && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleStartDraft}
              >
                <DiceIcon size={15} /> Start Draft
              </button>
            )}
            {isCommissioner && draft.status === 'in_progress' && (
              <button
                type="button"
                className="btn"
                onClick={handlePauseDraft}
                style={{
                  backgroundColor: 'var(--warning)',
                  color: 'var(--foreground)',
                  border: '1px solid var(--warning)'
                }}
              >
                <PauseIcon size={14} /> Pause
              </button>
            )}
            {isCommissioner && draft.status === 'paused' && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleResumeDraft}
              >
                <PlayIcon size={14} /> Resume
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Draft Board - minimized by default, toggle to expand */}
      <DraftBoard
        draft={draft}
        myTeam={myTeam}
        minimized={boardMinimized}
        onToggleMinimized={() => setBoardMinimized((b) => !b)}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '20px', marginTop: '20px' }}>
        {/* Main Draft Area */}
        <div>
        {/* Available Players */}
        <div className="card">
          <h3>Available Players ({filteredPlayers.length})</h3>
          
          {/* Search and Filters */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', margin: '16px 0' }}>
            <input
              className="form-control"
              type="text"
              placeholder="Search players..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="form-control"
              value={positionFilter}
              onChange={(e) => setPositionFilter(e.target.value)}
            >
              <option value="">All Positions</option>
              <option value="C">C</option>
              <option value="1B">1B</option>
              <option value="2B">2B</option>
              <option value="3B">3B</option>
              <option value="SS">SS</option>
              <option value="OF">OF</option>
              <option value="DH">DH</option>
              <option value="SP">SP</option>
              <option value="RP">RP</option>
            </select>
            <select
              className="form-control"
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
            >
              <option value="">All Teams</option>
              {['NYY', 'BOS', 'TB', 'TOR', 'BAL', 'LAD', 'SF', 'SD', 'ATL', 'PHI'].map(team => (
                <option key={team} value={team}>{team}</option>
              ))}
            </select>
          </div>

          {/* Players List */}
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {filteredPlayers.slice(0, 100).map((player, index) => (
              <div
                key={player.id}
                className="avail-row"
                onClick={() => setSelectedPlayer(player)}
                style={{
                  backgroundColor: selectedPlayer?.id === player.id ? 'var(--primary-soft)' : undefined
                }}
              >
                <span className="avail-rank">{index + 1}</span>
                <PlayerRow
                  player={player}
                  bare
                  onClick={(e) => {
                    e.stopPropagation();
                    openPlayerProfile(player, league.id || league.leagueId);
                  }}
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMakePick(player);
                  }}
                  disabled={!isMyTurn || draft.status !== 'in_progress'}
                  style={{
                    flexShrink: 0,
                    opacity: (!isMyTurn || draft.status !== 'in_progress') ? 0.5 : 1
                  }}
                >
                  Draft
                </button>
              </div>
            ))}
            {filteredPlayers.length === 0 && (
              <p className="text-secondary" style={{ textAlign: 'center', padding: '20px' }}>
                No players match your filters
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div>
        {/* My Team */}
        <div className="card">
          <h3>{myTeam?.logoEmoji} {myTeam?.name}</h3>
          <p className="hint" style={{ margin: '8px 0' }}>
            {myPicks.length} picks • {draft.settings.rounds - myPicks.length} remaining
          </p>
          {myTeam && draft?.status !== 'completed' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '12px 0', cursor: togglingAutoPick ? 'not-allowed' : 'pointer', opacity: togglingAutoPick ? 0.7 : 1 }}>
              <input
                type="checkbox"
                checked={isAutoPickOn}
                onChange={handleToggleAutoPick}
                disabled={togglingAutoPick}
                style={{ width: '18px', height: '18px', accentColor: 'var(--primary)' }}
              />
              <span style={{ fontSize: '14px', fontWeight: 500 }}>
                Auto-pick when it&apos;s my turn
              </span>
            </label>
          )}
          <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '12px' }}>
            {myPicks.length === 0 ? (
              <p className="text-secondary" style={{ textAlign: 'center', padding: '20px' }}>
                No picks yet
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {myPicks.map(pick => (
                  <div key={pick.pickNumber} style={{
                    padding: '8px',
                    backgroundColor: 'var(--surface-muted)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '14px'
                  }}>
                    <div style={{ fontWeight: '600' }}>{pick.playerName}</div>
                    <div className="hint">
                      Rd {pick.round}, Pick {pick.pickNumber} • {pick.playerPosition} - {pick.playerTeam}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Picks */}
        <div className="card">
          <h3>Recent Picks</h3>
          <div style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '12px' }}>
            {(draft.picks || []).slice(-10).reverse().map(pick => (
              <div key={pick.pickNumber} style={{
                padding: '10px',
                backgroundColor: 'var(--surface-muted)',
                borderRadius: 'var(--radius-sm)',
                marginBottom: '8px',
                borderLeft: '3px solid var(--primary)'
              }}>
                <div className="hint" style={{ marginBottom: '4px' }}>
                  Pick #{pick.pickNumber} • Round {pick.round}
                </div>
                <div style={{ fontWeight: '600', color: 'var(--primary)' }}>{pick.teamName}</div>
                <div style={{ fontSize: '14px', marginTop: '4px' }}>
                  {pick.playerName}
                </div>
                <div className="hint">
                  {pick.playerPosition} - {pick.playerTeam}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default DraftRoom;

